import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Mail, Phone, Calendar, Pencil, Video, Users, Star,
  TrendingUp, ChevronDown, BarChart3, Sparkles,
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import GaugeMeter from '../../components/ui/GaugeMeter';
import { clientService } from '../../services';
import exportService from '../../services/exportService';
import {
  getMetricBandInfo,
  getTEIInterpretation,
  getLAIInterpretation,
} from '../../utils/reportingConstants';
import toast from 'react-hot-toast';

// Shared chrome — matches admin detail pages.
const CARD_BASE =
  'rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.08)]';

const STAT_CHIP = {
  blue:    'bg-blue-50 text-blue-600 ring-1 ring-inset ring-blue-100 dark:bg-blue-500/10 dark:text-blue-400 dark:ring-blue-500/20',
  emerald: 'bg-emerald-50 text-emerald-600 ring-1 ring-inset ring-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/20',
  amber:   'bg-amber-50 text-amber-600 ring-1 ring-inset ring-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20',
  violet:  'bg-violet-50 text-violet-600 ring-1 ring-inset ring-violet-100 dark:bg-violet-500/10 dark:text-violet-400 dark:ring-violet-500/20',
};

const SECTION_HEADER_CHIP =
  'w-9 h-9 rounded-xl flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 text-neutral-600 dark:text-neutral-400 flex-shrink-0';

/**
 * Trainer Detail Page (Client)
 * Shows detailed information about a trainer including performance stats and analytics
 */

const TrainerDetailPage = () => {
  const { trainerId } = useParams();
  const navigate = useNavigate();
  const [trainer, setTrainer] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');

  useEffect(() => {
    fetchTrainerDetails();
  }, [trainerId]);

  useEffect(() => {
    if (trainer) {
      fetchTrainerAnalytics();
    }
  }, [trainerId, timeRange, trainer]);

  const fetchTrainerDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch trainer details (backend now calculates engagement and rating from actual data)
      const trainerResponse = await clientService.getTrainer(trainerId);

      if (trainerResponse.success) {
        const trainerData = trainerResponse.data;

        setTrainer({
          id: trainerData._id,
          firstName: trainerData.firstName,
          lastName: trainerData.lastName,
          name: `${trainerData.firstName} ${trainerData.lastName}`,
          email: trainerData.email,
          phone: trainerData.phone || 'Not provided',
          status: trainerData.status || 'active',
          role: trainerData.jobTitle || 'Trainer',
          expertise: trainerData.expertise || [],
          bio: trainerData.bio || 'No bio available',
          joinedDate: trainerData.createdAt,
          lastLogin: trainerData.lastLogin,
          avatar: trainerData.avatar,
          // Performance stats from backend (now calculated from actual session data)
          sessionsCompleted: trainerData.sessionsCompleted || 0,
          totalParticipants: trainerData.totalParticipants || 0,
          avgRating: trainerData.avgRating || 0,
          avgEngagement: trainerData.avgEngagement || 0,
        });
      } else {
        setError('Failed to load trainer details');
      }
    } catch (err) {
      console.error('Error fetching trainer details:', err);
      setError(err.message || 'Failed to load trainer details');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainerAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const response = await clientService.getTrainerAnalytics(trainerId, { period: timeRange });
      if (response.success) {
        setAnalytics(response.data);
      }
    } catch (err) {
      console.error('Error fetching trainer analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'warning';
      case 'suspended':
        return 'danger';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-4 flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-neutral-600">Loading trainer details...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !trainer) {
    return (
      <DashboardLayout>
        <div className="p-4">
          <button
            onClick={() => navigate('/client/trainers')}
            className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors mb-4 group"
          >
            <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm font-medium">Back to Trainers</span>
          </button>
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <p className="text-neutral-600 mb-4">{error || 'Trainer not found'}</p>
            <Button variant="primary" onClick={() => navigate('/client/trainers')}>
              Back to Trainers
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="w-full px-4 lg:px-6 py-6 space-y-5 pb-20 sm:pb-6">
        {/* Back link */}
        <button
          type="button"
          onClick={() => navigate('/client/trainers')}
          className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100 transition-colors group"
        >
          <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-0.5" />
          <span className="font-medium">Back to Trainers</span>
        </button>

        {/* Flat header — matches admin detail pages (UserDetail / OrganizationDetail) */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-1">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              {trainer.avatar ? (
                <img
                  src={trainer.avatar}
                  alt={trainer.name}
                  className="w-10 h-10 rounded-xl object-cover ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-700 flex-shrink-0"
                />
              ) : (
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700 text-white font-semibold text-base flex-shrink-0 ring-1 ring-inset ring-primary-300/30 shadow-[inset_0_-2px_4px_rgba(0,0,0,0.08)]">
                  {trainer.firstName?.charAt(0)}{trainer.lastName?.charAt(0)}
                </div>
              )}
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50 truncate">
                {trainer.name}
              </h1>
              <Badge variant={getStatusColor(trainer.status)}>{trainer.status}</Badge>
            </div>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 ml-13 sm:ml-0">{trainer.role}</p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2 text-sm text-neutral-600 dark:text-neutral-400">
              <span className="inline-flex items-center gap-1.5">
                <Mail size={14} strokeWidth={1.75} className="text-neutral-400 flex-shrink-0" />
                <span className="truncate">{trainer.email}</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Phone size={14} strokeWidth={1.75} className="text-neutral-400 flex-shrink-0" />
                <span>{trainer.phone}</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Calendar size={14} strokeWidth={1.75} className="text-neutral-400 flex-shrink-0" />
                <span>Joined {formatDate(trainer.joinedDate)}</span>
              </span>
            </div>
          </div>

          <div className="flex gap-2 flex-shrink-0">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/client/trainers/${trainerId}/edit`)}>
              <Pencil size={14} strokeWidth={1.75} className="mr-1.5" />
              Edit Trainer
            </Button>
          </div>
        </div>

        {/* Expertise Tags */}
        {trainer.expertise && trainer.expertise.length > 0 && (
          <div className={`${CARD_BASE} p-4`}>
            <p className="text-[11px] uppercase tracking-[0.06em] font-semibold text-neutral-400 dark:text-neutral-500 mb-2">Expertise</p>
            <div className="flex flex-wrap gap-2">
              {trainer.expertise.map((skill, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ring-1 ring-inset bg-primary-50 text-primary-700 ring-primary-200 dark:bg-primary-500/10 dark:text-primary-300 dark:ring-primary-500/30"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Performance Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Sessions Completed', value: trainer.sessionsCompleted, Icon: Video,    variant: 'blue' },
            { label: 'Total Participants', value: trainer.totalParticipants, Icon: Users,    variant: 'emerald' },
            { label: 'Avg Rating',         value: trainer.avgRating > 0 ? trainer.avgRating.toFixed(1) : 'N/A', Icon: Star, variant: 'amber' },
            { label: 'Avg Engagement',     value: trainer.avgEngagement > 0 ? `${Math.round(trainer.avgEngagement)}%` : 'N/A', Icon: TrendingUp, variant: 'violet' },
          ].map(({ label, value, Icon, variant }) => (
            <div key={label} className={`${CARD_BASE} p-5`}>
              <div className="flex items-center gap-2.5 mb-3">
                <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${STAT_CHIP[variant]}`}>
                  <Icon size={20} strokeWidth={1.75} />
                </span>
                <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{label}</span>
              </div>
              <span className="text-3xl font-semibold tabular-nums tracking-tight text-neutral-900 dark:text-neutral-50 leading-none">{value}</span>
            </div>
          ))}
        </div>

        {/* Bio Section */}
        <div className={`${CARD_BASE} p-5 sm:p-6`}>
          <div className="flex items-start gap-3 mb-3">
            <span className={SECTION_HEADER_CHIP}>
              <Sparkles size={16} strokeWidth={1.75} />
            </span>
            <div>
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 leading-tight">About</h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Background and expertise</p>
            </div>
          </div>
          <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">
            {trainer.bio || 'No bio information available for this trainer.'}
          </p>
        </div>

        {/* Performance Analytics Section */}
        {analytics && (
          <>
            {/* Time Range Filter */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3">
                <span className={SECTION_HEADER_CHIP}>
                  <BarChart3 size={16} strokeWidth={1.75} />
                </span>
                <div>
                  <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 leading-tight">Performance Analytics</h2>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Engagement, ratings, and session trends</p>
                </div>
              </div>
              <div className="relative">
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  className="appearance-none pl-3 pr-9 py-2 text-sm rounded-xl bg-neutral-50 dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 focus:outline-none focus:ring-2 focus:ring-primary-500/30 cursor-pointer text-neutral-700 dark:text-neutral-200"
                >
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="90d">Last 90 days</option>
                  <option value="all">All Time</option>
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
              </div>
            </div>

            {/* Performance Indices with GaugeMeter */}
            <Card>
              <div className="mb-4">
                <h3 className="text-sm sm:text-lg font-semibold text-neutral-900">Performance Indices</h3>
                <p className="text-[10px] sm:text-sm text-neutral-600 mt-1">Aggregated metrics across all sessions</p>
              </div>

              {analyticsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
                  {/* Trainer Effectiveness Index */}
                  <div className="flex flex-col items-center p-3 sm:p-4 bg-neutral-50 rounded-xl">
                    <GaugeMeter
                      value={analytics.indices?.trainerEffectiveness || analytics.metrics?.avgRating || 0}
                      maxValue={5}
                      label="Trainer Effectiveness"
                      band={getMetricBandInfo('tei', analytics.indices?.trainerEffectiveness || analytics.metrics?.avgRating || 0)?.label || 'N/A'}
                      color={getMetricBandInfo('tei', analytics.indices?.trainerEffectiveness || analytics.metrics?.avgRating || 0)?.color || 'gray'}
                      size="sm"
                    />
                    {(analytics.indices?.trainerEffectiveness || analytics.metrics?.avgRating) > 0 && (
                      <p className="mt-2 text-xs text-neutral-600 text-center max-w-[180px] leading-relaxed">
                        {getTEIInterpretation(analytics.indices?.trainerEffectiveness || analytics.metrics?.avgRating, 'trainer')}
                      </p>
                    )}
                  </div>

                  {/* Learning Advocacy Index */}
                  <div className="flex flex-col items-center p-3 sm:p-4 bg-neutral-50 rounded-xl">
                    <GaugeMeter
                      value={analytics.indices?.learningAdvocacy || 0}
                      maxValue={5}
                      label="Learning Advocacy"
                      band={getMetricBandInfo('lai', analytics.indices?.learningAdvocacy || 0)?.label || 'N/A'}
                      color={getMetricBandInfo('lai', analytics.indices?.learningAdvocacy || 0)?.color || 'gray'}
                      size="sm"
                    />
                    {analytics.indices?.learningAdvocacy > 0 && (
                      <p className="mt-2 text-xs text-neutral-600 text-center max-w-[180px] leading-relaxed">
                        {getLAIInterpretation(analytics.indices?.learningAdvocacy, 'trainer')}
                      </p>
                    )}
                  </div>

                  {/* Total Challenges */}
                  <div className="flex flex-col items-center justify-center p-3 sm:p-4 bg-neutral-50 rounded-xl">
                    <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-orange-100 flex items-center justify-center mb-2">
                      <span className="text-lg sm:text-2xl font-bold text-orange-600">
                        {analytics.indices?.totalChallenges || 0}
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm font-medium text-neutral-700">Total Challenges</p>
                    <p className="text-[10px] sm:text-xs text-neutral-500">Captured in sessions</p>
                  </div>

                  {/* Total Action Plans */}
                  <div className="flex flex-col items-center justify-center p-3 sm:p-4 bg-neutral-50 rounded-xl">
                    <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-green-100 flex items-center justify-center mb-2">
                      <span className="text-lg sm:text-2xl font-bold text-green-600">
                        {analytics.indices?.totalActionPlans || 0}
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm font-medium text-neutral-700">Action Plans</p>
                    <p className="text-[10px] sm:text-xs text-neutral-500">Created by participants</p>
                  </div>
                </div>
              )}
            </Card>

            {/* Engagement Trends + Top Sessions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Engagement Trends */}
              <Card>
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-sm sm:text-lg font-semibold text-neutral-900">Engagement Trends</h3>
                  <p className="text-[10px] sm:text-sm text-neutral-600 mt-1">Weekly engagement and participant count</p>
                </div>
                <div className="space-y-3 sm:space-y-4">
                  {(analytics.trends || []).map((trend, index) => (
                    <div key={index}>
                      <div className="flex justify-between text-xs sm:text-sm mb-1.5 sm:mb-2">
                        <span className="font-medium text-neutral-700">{trend.week}</span>
                        <div className="flex gap-2 sm:gap-4">
                          <span className="text-neutral-600">{trend.participants} participants</span>
                          <span className="text-primary-600 font-medium">{trend.engagement}%</span>
                        </div>
                      </div>
                      <div className="w-full bg-neutral-100 rounded-full h-3 overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(100, trend.engagement)}%` }}
                          transition={{ duration: 0.8, delay: index * 0.1 }}
                          className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Top Performing Sessions */}
              <Card>
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-sm sm:text-lg font-semibold text-neutral-900">Top Performing Sessions</h3>
                  <p className="text-[10px] sm:text-sm text-neutral-600 mt-1">Sessions ranked by engagement rate</p>
                </div>
                <div className="space-y-3">
                  {(analytics.sessionPerformance || [])
                    .sort((a, b) => (b.engagement || 0) - (a.engagement || 0))
                    .slice(0, 5)
                    .map((session, index) => (
                      <div
                        key={session.id}
                        className="p-3 sm:p-4 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/client/sessions/${session.id}`)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 bg-primary-100 text-primary-700 font-bold rounded-full text-xs sm:text-sm flex-shrink-0">
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <p className="font-medium text-neutral-900 text-xs sm:text-sm truncate">{session.name}</p>
                              <Badge
                                variant={
                                  session.engagement >= 90 ? 'success' : session.engagement >= 75 ? 'info' : 'warning'
                                }
                              >
                                {session.engagement}%
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 sm:gap-3 text-[10px] sm:text-xs text-neutral-500 mt-0.5">
                              <span>{formatDate(session.date)}</span>
                              <span>{session.participants} participants</span>
                              {session.rating > 0 && <span>Rating: {session.rating}/5.0</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </Card>
            </div>

            {/* Additional Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Training Hours */}
              <Card>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-neutral-600">Total Training Hours</p>
                    <p className="text-xl sm:text-3xl font-bold text-neutral-900 mt-1 sm:mt-2">
                      {analytics.metrics?.totalHours?.toFixed(1) || '0.0'}
                    </p>
                    <p className="text-[10px] sm:text-sm text-neutral-500 mt-1">
                      Across {analytics.metrics?.completedSessions || 0} sessions
                    </p>
                  </div>
                  <div className="p-3 sm:p-4 bg-indigo-100 rounded-lg">
                    <svg className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </Card>

              {/* Success Rate */}
              <Card>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-neutral-600">Session Success Rate</p>
                    <p className="text-xl sm:text-3xl font-bold text-neutral-900 mt-1 sm:mt-2">
                      {analytics.metrics?.completedSessions > 0 && analytics.metrics?.totalSessions > 0
                        ? Math.round((analytics.metrics.completedSessions / analytics.metrics.totalSessions) * 100)
                        : 0}%
                    </p>
                    <p className="text-[10px] sm:text-sm text-neutral-500 mt-1">
                      {analytics.metrics?.completedSessions || 0} of {analytics.metrics?.totalSessions || 0} completed
                    </p>
                  </div>
                  <div className="p-3 sm:p-4 bg-green-100 rounded-lg">
                    <svg className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </Card>
            </div>

            {/* Recent Feedback */}
            {analytics.recentFeedback && analytics.recentFeedback.length > 0 && (
              <Card>
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-sm sm:text-lg font-semibold text-neutral-900">Recent Participant Feedback</h3>
                  <p className="text-[10px] sm:text-sm text-neutral-600 mt-1">What participants are saying about this trainer</p>
                </div>
                <div className="space-y-3 sm:space-y-4">
                  {analytics.recentFeedback.map((feedback) => (
                    <div key={feedback.id} className="p-3 sm:p-4 border border-neutral-200 rounded-lg">
                      <div className="flex items-start justify-between mb-2 sm:mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center text-white text-xs sm:text-base font-semibold">
                            {feedback.participantName?.charAt(0) || 'A'}
                          </div>
                          <div>
                            <p className="text-xs sm:text-sm font-medium text-neutral-900">{feedback.participantName || 'Anonymous'}</p>
                            <p className="text-[10px] sm:text-xs text-neutral-500">{feedback.sessionName}</p>
                          </div>
                        </div>
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <svg
                              key={star}
                              className={`w-4 h-4 ${star <= feedback.rating ? 'text-amber-400 fill-current' : 'text-neutral-300'}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                            </svg>
                          ))}
                        </div>
                      </div>
                      <p className="text-xs sm:text-sm text-neutral-700">{feedback.comments}</p>
                      {feedback.submittedAt && (
                        <p className="text-xs text-neutral-500 mt-2">{formatDate(feedback.submittedAt)}</p>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>
        )}

        {/* Actions */}
        <Card title="Quick Actions">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => navigate(`/client/sessions/new?trainer=${trainerId}`)}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Assign New Session
            </Button>
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => navigate(`/client/reports?trainer=${trainerId}`)}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              View Report
            </Button>
            <Button
              variant="outline"
              className="justify-start"
              onClick={async () => {
                try {
                  const trainerName = `${trainer.firstName}-${trainer.lastName}`;
                  await exportService.exportTrainerReportCSV(trainer.id, trainerName);
                  toast.success('Report downloaded successfully');
                } catch (error) {
                  toast.error('Failed to download report');
                }
              }}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download Report
            </Button>
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => {
                navigator.clipboard.writeText(trainer.email);
                toast.success('Email copied to clipboard');
              }}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
              </svg>
              Copy Email
            </Button>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default TrainerDetailPage;
